import { Effect, Option } from 'effect';

import {
  completeOrder,
  CompleteOrderError,
  createPaymentRequestForOrder,
  updateOrder,
  UpdateOrderError,
  UpdatePaymentError,
  updatePaymentInOrder,
  UpdateShippingError,
  updateShippingInOrder,
} from '../../bind/order';
import type { OrderDetail, OrderUpdateCart } from '../../schema/openapi/backend';
import type { OrderShipmentStorageT } from '../../service/orderShipment/OrderShipment';
import { getOrCreateOrderToken } from '../../utils/cookies/getOrCreateOrderToken';
import { removeOrderShipment } from '../../utils/cookies/orderShipment';
import { removeOrderToken } from '../../utils/cookies/removeOrderToken';
import { TenantConfig } from '../../service/tenant/TenantConfig';
import { removeTokenDueToViolation } from '../../utils/error/backendApiErrorHandlers';
import { orderSubmission } from './orderSubmission';

export type ProcessOrderData = {
  data: OrderUpdateCart;
  orderData: OrderDetail | null;
  orderShipment: OrderShipmentStorageT | null;
  note: string;
  heurekaConsentForbidden: boolean;
};

export default function processOrder({
  data,
  orderData,
  orderShipment,
  note,
  heurekaConsentForbidden,
}: ProcessOrderData) {
  return Effect.gen(function* () {
    const { locale } = yield* TenantConfig;

    const submission = yield* orderSubmission(
      Option.fromNullOr(orderData),
      Option.fromNullOr(orderShipment)
    );
    const token = yield* getOrCreateOrderToken;

    const checkoutDetailsData = {
      ...data,
      couponCode: orderData?.promotionCoupon?.code ?? null,
    };

    yield* updateOrder(token, checkoutDetailsData).pipe(
      Effect.catchTag(UpdateOrderError.Tags.Violation, removeTokenDueToViolation)
    );

    yield* updateShippingInOrder(token, submission.shipmentId, {
      shippingMethod: submission.shippingMethod,
      placementId: Option.getOrNull(submission.placementId),
    }).pipe(Effect.catchTag(UpdateShippingError.Tags.Violation, removeTokenDueToViolation));

    yield* updatePaymentInOrder(token, submission.paymentId, {
      paymentMethod: submission.paymentMethod,
    }).pipe(Effect.catchTag(UpdatePaymentError.Tags.Violation, removeTokenDueToViolation));

    // ATTENTION: negation on purpose! heurekaConsentForbidden -> surveyOptIn
    yield* completeOrder(token, note, !heurekaConsentForbidden).pipe(
      Effect.catchTag(CompleteOrderError.Tags.Violation, removeTokenDueToViolation)
    );

    yield* removeOrderShipment;
    yield* removeOrderToken;

    const paymentResponse = yield* createPaymentRequestForOrder(
      token,
      submission.paymentId,
      submission.paymentMethod,
      locale
    );

    return { completedOrderToken: token, paymentResponse };
  });
}
